import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PaymentSuccessModal from '../psp/PaymentSuccessModal';
import { useNavigate } from 'react-router';
import { safeLocalStorage } from '../utils/storage';

interface PaymentSuccessContextType {
  showPaymentSuccess: (creditAmount?: number, status?: 'success' | 'pending') => void;
}

const PaymentSuccessContext = createContext<PaymentSuccessContextType | undefined>(undefined);

export const usePaymentSuccess = () => {
  const context = useContext(PaymentSuccessContext);
  if (!context) {
    throw new Error('usePaymentSuccess must be used within PaymentSuccessProvider');
  }
  return context;
};

interface PaymentSuccessProviderProps {
  children: React.ReactNode;
}

export const PaymentSuccessProvider: React.FC<PaymentSuccessProviderProps> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState<number | undefined>(undefined);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'pending'>('success');
  const navigate = useNavigate();

  const showPaymentSuccess = useCallback((credits?: number, status: 'success' | 'pending' = 'success') => {
    // Try to get credits from parameter or localStorage
    const purchasedCredits = credits || Number(safeLocalStorage.getItem('purchasedCredits'));
    if (purchasedCredits) {
      setCreditAmount(purchasedCredits);
    }
    setPaymentStatus(status);
    setShowModal(true);
  }, []);

  const handleClose = () => {
    setShowModal(false);
    setCreditAmount(undefined);
    setPaymentStatus('success');
    // Clean up localStorage
    safeLocalStorage.removeItem('purchasedCredits');
  };

  const handleViewCredits = () => {
    setShowModal(false);
    setCreditAmount(undefined);
    setPaymentStatus('success');
    // Clean up localStorage
    safeLocalStorage.removeItem('purchasedCredits');
    navigate('/credits');
  };

  const value = useMemo(() => ({ showPaymentSuccess }), [showPaymentSuccess]);

  return (
    <PaymentSuccessContext.Provider value={value}>
      {children}
      <PaymentSuccessModal
        show={showModal}
        onClose={handleClose}
        onContinue={handleClose}
        onViewCredits={handleViewCredits}
        creditAmount={creditAmount}
        status={paymentStatus}
      />
    </PaymentSuccessContext.Provider>
  );
};